import { fetchProductSubscription } from 'actions/LuxPlusActions'
import { FormattedMessage, useIntl } from 'react-intl'
import AccountAccessModal from 'components/Account/AccountAccess/AccountAccessModal'
import type { AccountAccessModalResult } from 'components/Account/AccountAccess/AccountAccessModal.types'
import CustomerSupportBlock from 'components/Common/CustomerSupportBlock'
import Image from 'components/Common/Image'
import LayoutContainer from 'components/Common/LayoutContainer'
import LoadingIndicator from 'components/Common/Loading/LoadingIndicator'
import Pane from 'components/Common/Pane'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import TextButton from 'LuxKit/Button/TextButton'
import Divider from 'LuxKit/Divider'
import LineMapMarkerIcon from 'LuxKit/Icons/line/LineMapMarkerIcon'
import TextLink from 'LuxKit/TextLink'
import BodyText from 'LuxKit/Typography/BodyText'
import Heading from 'LuxKit/Typography/Heading'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import Group from 'components/utils/Group'
import { mediaQueryUp } from 'components/utils/breakpoint'
import ModalContext from 'contexts/ModalContext'
import ProductPaletteProvider from 'contexts/ProductPaletteContext'
import { useOfferPackagePrice } from 'hooks/Offers/useOfferPrice'
import { useAppDispatch } from 'hooks/reduxHooks'
import { LUXURY_PLUS } from 'luxPlus/constants/base'
import { rem } from 'polished'
import { useCallback, useContext, useEffect, useMemo } from 'react'
import { connect } from 'react-redux'
import { selectLoggedIn } from 'selectors/accountSelectors'
import styled from 'styled-components'
import LuxPlusBenefitsList from '../LuxPlusBenefitsList'
import LuxPlusNotificationBanner from '../Banners/LuxPlusNotificationBanner'
import LuxPlusHiddenOfferImageContainer from './LuxPlusHiddenOfferImageContainer'
import useHiddenOfferClickHandler from './useHiddenOfferClickHandler'
import OfferPageLabelGroup from 'components/OfferPage/OfferPageLabelGroup'
import { earlyAccessRestrictedToMembers } from 'luxPlus/lib/utils'
import useRebrandSuccessDarkColour from 'hooks/useRebrandSuccessDarkColour'

const SummaryColumn = styled.div`
  display: none;

  ${mediaQueryUp.tablet} {
    display: block;
    height: 100%;
  }
`

const SummaryBlock = styled.div`
  ${mediaQueryUp.tablet} {
    position: sticky;
    top: ${rem(12)};
  }
`

const SummaryPane = styled(Pane)`
  position: relative;

  > * + * {
    margin-top: ${rem(12)};
  }
`

interface MappedProps {
  subscription: App.ProductSubscription
  isLoggedIn: boolean
}

interface Props extends MappedProps {
  offer: App.Offer | App.OfferSummary
}

function LuxPlusHiddenOfferOfferContent(props: Props) {
  const accentColour = useRebrandSuccessDarkColour()
  const { offer, subscription, isLoggedIn } = props
  const intl = useIntl()

  const pkg = offer.lowestPricePackage!
  const {
    valueOffPercent,
  } = useOfferPackagePrice(pkg, offer, { showMemberPrice: true })

  /**
   * If the locationHeading and locationSubheading are the same, only display the locationSubheading
   * e.g. Singapore, Singapore -> Singapore
   */
  const formattedLocationHeading = useMemo(() => {
    if (offer.locationHeading === offer.locationSubheading) return offer.locationSubheading
    return intl.formatMessage(
      {
        id: 'b6kcJB4QQjbq-pNOqasb',
        defaultMessage: '{locationHeading}, {locationSubheading}',
        description: 'LuxPlus hidden offer content location heading and subheading separated by comma',
      },
      { locationHeading: offer.locationHeading, locationSubheading: offer.locationSubheading },
    )
  }, [offer.locationHeading, offer.locationSubheading, intl])

  const dispatch = useAppDispatch()
  const showModal = useContext(ModalContext)
  const openLogin = useCallback(() => {
    return showModal<AccountAccessModalResult>(<AccountAccessModal />)
    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const onHandleHiddenOffer = useHiddenOfferClickHandler(offer)

  useEffect(() => {
    dispatch(fetchProductSubscription())
  }, [dispatch])

  return <Pane>
    <VerticalSpacer gap={16}>
      <LuxPlusNotificationBanner hideCta>
        {intl.formatMessage({
          id: 'BSMYs8uP3atwexfXMh7H',
          defaultMessage: 'Members get exclusive access to this hidden offer',
          description: 'Notification banner text for exclusive hidden offer access for LuxPlus+ members',
        })}
      </LuxPlusNotificationBanner>
      <LayoutContainer>
        <VerticalSpacer gap={24}>
          <Group direction="vertical" gap={16} tabletGap={32}>
            <LuxPlusHiddenOfferImageContainer tabletRatio="53:20" ratio="3:2">
              <Image
                id={offer.image.id ?? ''}
                width={80}
                height={43}
                quality="low"
                fit="center"
                alt={intl.formatMessage(
                  {
                    id: 'WmQVRdJ9EgSTCmWkFcq0',
                    defaultMessage: 'Exclusive hidden offer in {location}',
                    description: 'Alt text for the hidden offer image showing the destination location',
                  },
                  { location: offer.locationSubheading },
                )}
              />
            </LuxPlusHiddenOfferImageContainer>
            <Group direction="horizontal" horizontalAlign="space-between" gap={48}>
              <VerticalSpacer gap={16}>
                {earlyAccessRestrictedToMembers(offer) && <div>
                  <OfferPageLabelGroup offer={offer} />
                </div>}
                <VerticalSpacer gap={4}>
                  <Group direction="horizontal" gap={8}>
                    <LineMapMarkerIcon size="S" colour="primary" />
                    <BodyText variant="large" weight="bold">
                      {formattedLocationHeading}
                    </BodyText>
                  </Group>
                  <Heading variant="heading3">
                    <FormattedMessage
                      id="JXuBfxHWgvdf2MyuK4_A"
                      defaultMessage="<highlighted>Members save up to {valueOffPercent}%</highlighted> on this hidden offer in {location}"
                      description="Heading showing member savings percentage on hidden hotel offer page"
                      values={{
                        valueOffPercent,
                        location: formattedLocationHeading,
                        highlighted: (chunks) => <Heading variant="heading3" colour={accentColour} as="span">{chunks}</Heading>,
                      }}
                    />
                  </Heading>
                </VerticalSpacer>
                {subscription.fetching && <LoadingIndicator inline>{intl.formatMessage({
                  id: 'hMGIRRdV5ALNwEFNgZON',
                  defaultMessage: 'Loading...',
                  description: 'Loading indicator text while subscription details are being fetched',
                })}
                </LoadingIndicator>}
                {subscription.error && <BodyText variant="medium">{intl.formatMessage({
                  id: 'QxNtic_Ud-Oy76BNgSbV',
                  defaultMessage: 'There was an error loading this offer’s details. Please try again later',
                  description: 'Error message when hidden offer details fail to load',
                })}
                </BodyText>}
                {subscription.offers && <VerticalSpacer gap={8}>
                  <BodyText variant="medium" weight="bold">
                    {intl.formatMessage({
                      id: 'cu7k3pCEvjMijJMQtcSd',
                      defaultMessage: 'Plus you get these benefits all year round:',
                      description: 'Section heading for yearly benefits on hidden offer page',
                    })}
                  </BodyText>
                  <LuxPlusBenefitsList kind="long" benefits={subscription.offers[0].benefits} />
                </VerticalSpacer>}
                <CSSBreakpoint max="mobile">
                  <ProductPaletteProvider palette="lux-plus">
                    <TextButton variant="default" kind="primary" onClick={onHandleHiddenOffer} fit="mobile-full-width">
                      {intl.formatMessage({
                        id: 'dt3TqIEZxpB7sqsHM8cA',
                        defaultMessage: 'Unlock this offer via Free Preview',
                        description: 'CTA button text to unlock hidden offer via LuxPlus+ free preview on mobile',
                      })}
                    </TextButton>
                  </ProductPaletteProvider>
                </CSSBreakpoint>
              </VerticalSpacer>
              <CSSBreakpoint min="tablet">
                <SummaryColumn>
                  <SummaryBlock>
                    <SummaryPane shadow="bottom-small" border="light-foreground" borderRadius="XS" padding="28 20">
                      <BodyText variant="medium" colour="lux-plus" weight="bold">
                        {intl.formatMessage({
                          id: 'WfB3oCyPHJsa2x9EZF1j',
                          defaultMessage: 'Members get exclusive access to this hidden offer',
                          description: 'Summary pane text for exclusive hidden offer access for LuxPlus+ members',
                        })}
                      </BodyText>
                      <ProductPaletteProvider palette="lux-plus">
                        <TextButton variant="default" kind="primary" onClick={onHandleHiddenOffer} fit="full-width">
                          {intl.formatMessage({
                            id: '85eJCcoqm4F6nrlKXbkv',
                            defaultMessage: 'Unlock this offer via Free Preview',
                            description: 'CTA button text to unlock hidden offer via LuxPlus+ free preview on desktop',
                          })}
                        </TextButton>
                      </ProductPaletteProvider>
                      {!isLoggedIn && <BodyText variant="medium" colour="normal-contrast">
                        <FormattedMessage
                          id="lYTDW7ENW-NeCKPFnAOK"
                          defaultMessage="Already a {programName} member? <link>Sign in</link>"
                          description="Sign-in prompt for existing LuxPlus+ members on hidden offer page"
                          values={{
                            programName: LUXURY_PLUS.PROGRAM_NAME,
                            link: (chunks) => <TextLink size="medium" onClick={openLogin}>{chunks}</TextLink>,
                          }}
                        />
                      </BodyText>}
                    </SummaryPane>
                  </SummaryBlock>
                </SummaryColumn>
              </CSSBreakpoint>
            </Group>
          </Group>
          <Divider kind="primary" />
          <CustomerSupportBlock
            heading={intl.formatMessage({
              id: 'NNW9Qi_0L_cqNoLiaGWr',
              defaultMessage: 'Our team are available 24/7',
              description: 'Customer support heading on hidden offer page in the LuxPlus membership section',
            })}
            description={intl.formatMessage({
              id: 'b4NYlTWWZ1eC1Vyz_lCa',
              defaultMessage: 'Call our dedicated customer service team anytime.',
              description: 'Customer support description on hidden offer page in the LuxPlus membership section',
            })}
          />
        </VerticalSpacer>
      </LayoutContainer>
    </VerticalSpacer>
  </Pane>
}

const mapStateToProps = (state: App.State): MappedProps => ({
  subscription: state.luxPlus.products.subscription,
  isLoggedIn: selectLoggedIn(state),
})

export default connect(mapStateToProps)(LuxPlusHiddenOfferOfferContent)
